/// <reference types="./types/globals.d.ts" />

import { handleScreenshot } from "./utils";
import type {
  ExperienceSettings,
  ViewerMode,
  QualityLevel,
  AssetType,
  SessionValidationResponse
} from './types';

const KEEP_LOADER_VISIBLE_FOR_TUNING = false;

/**
 * Viewport dimensions
 */
interface ViewportDimensions {
  width: number;
  height: number;
}

type ViewerConsoleElement = HTMLElement & {
  mode?: ViewerMode;
  getSceneData?: () => unknown;
};

/**
 * Get viewport dimensions with fallbacks for WebView scenarios
 */
function getViewportDimensions(): ViewportDimensions {
  const width =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth ||
    screen.width;

  const height =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight ||
    screen.height;

  return { width, height };
}

/**
 * Resolve size to CSS string (pixels or 100%)
 */
function resolveSize(n: number): string {
  return n && n > 0 ? `${n}px` : '100%';
}

/**
 * Get the live viewer element rendered by the frame
 */
function getViewerElement(): ViewerConsoleElement | null {
  return document.getElementById('replate-viewer') as ViewerConsoleElement | null;
}

/**
 * Read the current mode shown in `replate:get-scene` directly from the frame
 */
function getCurrentSceneMode(): ViewerMode | null {
  const viewer = getViewerElement();
  const mode = viewer?.mode ?? viewer?.getAttribute('mode');
  return typeof mode === 'string' && mode.length > 0 ? mode as ViewerMode : null;
}

/**
 * Read the full current scene payload directly from the live frame
 */
function getCurrentSceneData(): unknown | null {
  const viewer = getViewerElement();
  return viewer?.getSceneData?.() ?? null;
}

/**
 * Register small console helpers for debugging embedded frames
 */
function registerConsoleHelpers(): void {
  window.replateFrame = {
    ...window.replateFrame,
    getScene: getCurrentSceneData,
    getSceneMode: getCurrentSceneMode,
    getViewer: getViewerElement,
  };
}

/**
 * Get asset type from filename
 */
function getAssetType(filename: string | null): AssetType {
  if (!filename) return 'glb';
  const extension = filename.split('.').pop()?.toLowerCase();
  if (extension === 'replate') return 'replate';
  if (extension === 'ply') return 'ply';
  if (extension === 'gltf') return 'gltf';
  return 'glb';
}

/**
 * Emit event to React Native WebView
 */
function emitToReactNative(event: string): void {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(event);
  }
  if (window.parent !== window) {
    window.parent.postMessage(event, "*");
  }
}

/**
 * Show error page (placeholder implementation)
 */
function showErrorPage(code: string = 'ERR_GENERIC'): void {
  console.error('Viewer error occurred', code);

  if (document.getElementById('replate-error-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'replate-error-overlay';
  overlay.style.cssText = [
    'position: fixed',
    'top: 0',
    'left: 0',
    'width: 100vw',
    'height: 100vh',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'background: #111111',
    'color: #ffffff',
    'font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    'z-index: 2147483647',
    'text-align: center',
    'padding: 24px',
    'box-sizing: border-box'
  ].join(';');

  const inner = document.createElement('div');
  inner.innerHTML = `
    <div style="font-size: 18px; font-weight: 600; margin-bottom: 8px;">Something went wrong</div>
    <div style="font-size: 13px; opacity: 0.7;">Error code: ${code}</div>
  `;
  overlay.appendChild(inner);

  document.body.appendChild(overlay);
}

/**
 * Normalize loading method value
 * 0 = poster/full wait, 1 = progressive MEANS preview
 */
function normalizeLoadingMethod(
  methodParam: string | null,
  serverMethod: number | null | undefined
): number {
  const fromString = (value: string | null): number | null => {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'progressive') return 1;
    if (normalized === 'poster' || normalized === 'default') return 0;
    const parsed = Number.parseInt(normalized, 10);
    if (Number.isFinite(parsed) && (parsed === 0 || parsed === 1)) {
      return parsed;
    }
    return null;
  };

  const urlChoice = fromString(methodParam);
  if (urlChoice !== null) {
    return urlChoice;
  }

  if (serverMethod === 0 || serverMethod === 1) {
    return serverMethod;
  }

  return 0;
}

/**
 * Resolve poster URL to an absolute URL when backend returns a relative path
 */
function resolvePosterUrl(poster: string | null, assetUrl: string): string | null {
  if (!poster) return null;
  const trimmed = poster.trim();
  if (!trimmed) return null;

  // Already absolute or protocol-relative URL
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  // Resolve relative poster path against loaded asset URL first
  if (assetUrl) {
    try {
      return new URL(trimmed, assetUrl).toString();
    } catch {
      // Fall through to page origin fallback
    }
  }

  try {
    return new URL(trimmed, window.location.origin).toString();
  } catch {
    return trimmed;
  }
}

/**
 * Escape HTML attribute value
 */
function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Apply background image to loader overlay
 */
function setLoaderBackgroundImage(previewUrl: string | null): void {
  const loaderContainer = document.getElementById('replate-loader-container');
  const loaderBackground = loaderContainer?.querySelector('.replate-loader-bg') as HTMLElement | null;
  if (!loaderContainer) return;

  if (!previewUrl) {
    loaderContainer.style.removeProperty('--loader-bg-image');
    loaderContainer.style.removeProperty('background-image');
    if (loaderBackground) {
      loaderBackground.style.backgroundImage = 'none';
    }
    return;
  }

  const sanitizedUrl = previewUrl.replace(/"/g, '\\"');
  const cssBackground = `url("${sanitizedUrl}")`;
  loaderContainer.style.setProperty('--loader-bg-image', cssBackground);
  if (loaderBackground) {
    loaderBackground.style.backgroundImage = cssBackground;
  }
}

/**
 * Hide loader with animation
 */
function hideLoader(): void {
  if (KEEP_LOADER_VISIBLE_FOR_TUNING) {
    return;
  }

  const loaderContainer = document.getElementById('replate-loader-container');
  if (loaderContainer) {
    loaderContainer.classList.add('is-hidden');
    loaderContainer.style.pointerEvents = 'none';
    loaderContainer.style.opacity = '0';
    loaderContainer.style.visibility = 'hidden';
    setTimeout(() => {
      loaderContainer.style.display = 'none';
    }, 220);
  }
}

/**
 * Validate session with backend API
 * Now always calls the API for validation
 */
async function validateSession(
  asset: string | null,
  item: string | null,
  referrer: string,
  authorization: string | null,
  mode: string
): Promise<SessionValidationResponse> {
  console.log({ asset, item, referrer, authorization, mode });

  return new Promise(async (resolve, reject) => {
    console.log('[DEBUG] Request is made through another website, needs to be validated through authorization.');

    try {
      const response = await fetch(
        `https://api.${import.meta.env.VITE_DOMAIN}/website/frame/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', authorization: authorization || '' },
          body: JSON.stringify({ asset, item, referrer, mode })
        }
      );

      const result = await response.json();
      if (!result || !result.url) {
        reject(new Error('[ERROR] The server couldnt handle the request, check your implementation.'));
        return;
      }

      console.log({
        asset: asset,
        assetUrl: result.url,
        assetType: result.type,
        poster: result.poster,
        loadingMethod: result.method,
        valid: result.valid,
        saveItemAnalytics: result.saveAssetAnalytics,
        saveAssetAnalytics: result.saveItemAnalytics
      });

      resolve({
        asset: asset || '',
        assetUrl: result.url,
        assetType: result.type,
        valid: result.valid,
        saveItemAnalytics: result.saveAssetAnalytics,
        saveAssetAnalytics: result.saveItemAnalytics,
        animation: result.animation,
        poster: result.poster || null,
        loadingMethod: result.method ?? null
      });
    } catch (error) {
      reject(new Error(`[ERROR] Failed to validate session: ${(error as Error).message}`));
    }
  });
}

/**
 * Initialize the viewer component
 */
async function initComponent(): Promise<void> {
  const urlParams = new URLSearchParams(window.location.search);

  const body = document.querySelector('body')!;
  const loaderContainer = document.getElementById('replate-loader-container');
  const viewerContainer = document.getElementById('viewer-container');
  const fullScreenContainer = document.getElementById('fullscreen-flex');

  if (!loaderContainer || !viewerContainer || !fullScreenContainer) {
    throw new Error('Required container elements not found');
  }

  const mode = (urlParams.get('mode') || 'web') as ViewerMode;
  const token = urlParams.get('token');

  // Default settings (matching original OrbitCamera distance ~1)
  const settings: ExperienceSettings = {
    background: { color: [0, 0, 0, 1] },
    camera: {
      fov: 15,  // Match unified viewer default (was 45 for old PlayCanvas)
      target: [0, 0, 0],
      up: [0, -1, 0],  // Inverted Y-up to match Gaussian splat coordinate system
      near: 0.1,
      far: 1000,
      scale: 3.5,
      position: [6.5, -4.875, 0],  // Negative Y hemisphere with inverted up vector
      startAnim: 'animTrack',
      animTrack: undefined
    },
    animTracks: []
  };

  // Adjust settings based on mode
  if (mode === 'app' || mode === 'screenshotbg') {
    settings.background.color = [17 / 255, 17 / 255, 17 / 255, 1];
  }

  // Removed green screen for screenshot mode
  /*if (mode === 'screenshot') {
    settings.background.color = [4 / 255, 244 / 255, 4 / 255, 1];
  }*/

  // Set body background
  const [r, g, b, a] = settings.background.color;
  body.style.background = `rgba(${r * 255},${g * 255},${b * 255},${a * 255})`;

  console.log(`rgba(${r * 255},${g * 255},${b * 255},${a * 255})`);

  // Let CSS/layout own sizing to avoid viewport-vs-layout mismatches on resize.
  [loaderContainer, viewerContainer, fullScreenContainer].forEach((element) => {
    element.style.width = "100%";
    element.style.height = "100%";
  });

  // Get URL parameters
  const assetParam = urlParams.get('asset');
  const authorization = urlParams.get('auth');
  const item = urlParams.get('item');
  const background = urlParams.get('background');
  const preset = urlParams.get('preset');
  const loadingMethodParam = urlParams.get('loadingmethod') || urlParams.get('loadingMethod');
  const posterParam = urlParams.get('poster');
  const gaussianQuantityParam = urlParams.get('gaussianquantity');

  // Safely parse referrer with error handling
  let referrer = 'unknown';
  if (document.referrer) {
    try {
      referrer = new URL(document.referrer).origin;
    } catch (e) {
      console.warn('[WARN] Invalid referrer URL:', document.referrer, e);
      referrer = 'invalid';
    }
  }

  let asset = assetParam || '';
  let assetUrl = '';
  let assetType: AssetType = 'replate';
  let valid = true;
  let saveAssetAnalytics = false;
  let saveItemAnalytics = false;
  let animation: SessionValidationResponse['animation'] | undefined;
  let poster: string | null = null;
  let loadingMethod = normalizeLoadingMethod(loadingMethodParam, null);

  if (mode !== 'editor') {
    try {
      const validatedSession = await validateSession(assetParam, item, referrer, authorization, mode);
      asset = validatedSession.asset;
      assetUrl = validatedSession.assetUrl;
      assetType = validatedSession.assetType;
      valid = validatedSession.valid;
      saveAssetAnalytics = validatedSession.saveAssetAnalytics;
      saveItemAnalytics = validatedSession.saveItemAnalytics;
      animation = validatedSession.animation;
      poster = validatedSession.poster || null;
      loadingMethod = normalizeLoadingMethod(loadingMethodParam, validatedSession.loadingMethod);
    } catch (error) {
      console.error('[ERROR] Session validation failed:', error);
      hideLoader();
      showErrorPage('ERR_VALIDATION');
      emitToReactNative('viewerError');
      return;
    }
  }

  const rawPosterUrl = mode === 'editor' ? posterParam : (poster || posterParam);
  const loaderPreviewUrl = resolvePosterUrl(rawPosterUrl, assetUrl);
  const posterAttribute = escapeHtmlAttribute(loaderPreviewUrl || '');
  setLoaderBackgroundImage(loaderPreviewUrl);

  // Adjust camera FOV based on asset type and mode
  if (mode !== 'editor' && assetType === 'glb') {
    settings.camera.fov = 15;
    settings.camera.up = [0, 1, 0];  // Standard Y-up for meshes
    // Make the camera higher
    settings.camera.position = [6.5, 6, 6];
    if (mode === 'app') {
      settings.camera.fov = 25;
    }
  }

  // Dynamic import - unified viewer handles all asset types
  if (mode === 'editor' || assetType === 'glb' || assetType === 'ply' || assetType === 'replate') {
    await import('./spark-unified-viewer.js');
  } else {
    throw new Error('Invalid asset format, check tha all the frame params are correct and well formatted.');
  }

  // Add animation track if provided
  if (animation) {
    settings.camera.animTrack = animation.name;
    settings.animTracks = [animation];
  }

  // Get quality parameter
  const quality = (urlParams.get('quality') || 'high') as QualityLevel;
  const gaussianQuantity = gaussianQuantityParam
    ? Math.max(0, Math.min(1, Number.parseFloat(gaussianQuantityParam)))
    : null;

  // Create viewer element dynamically
  viewerContainer.innerHTML = `
    <replate-viewer
      id='replate-viewer'
      class='model-viewer opacity-component'
      mode='${mode}'
      quality='${quality}'
      ${gaussianQuantity !== null && Number.isFinite(gaussianQuantity) ? `gaussianquantity='${gaussianQuantity}'` : ''}
      assettype='${mode === 'editor' || assetType === 'replate' || assetType === 'ply' ? 'splat' : 'mesh'}'
      src='${assetUrl}'
      settings='${JSON.stringify(settings)}'
      item='${item || ''}'
      asset='${asset}'
      valid='${valid}'
      background='${background || ''}'
      preset='${preset || ''}'
      saveAssetAnalytics='${saveAssetAnalytics}'
      saveItemAnalytics='${saveItemAnalytics}'
      auth='${authorization || ''}'
      loadingmethod='${loadingMethod}'
      poster='${posterAttribute}'
      style='width: 100%; height: 100%; display: block;'
    >
    </replate-viewer>`;

  // Setup event listeners (only fires once now)
  window.addEventListener(
    'replate:viewer:loaded',
    () => {
      hideLoader();
      emitToReactNative('viewerLoad');

      if (mode === 'screenshotweb') {
        console.log('Taking screenshot in 1 second...');
        setTimeout(() => {
          handleScreenshot({ asset, token: token || undefined, VITE_DOMAIN: import.meta.env.VITE_DOMAIN });
        }, 1000);
      } else if (mode === 'screenshotapp') {
        window.addEventListener('message', (event) => {
          let data: any;
          try {
            data = JSON.parse(event.data as string);
          } catch {
            return;
          }
          if (data && data.type === 'TAKE_SCREENSHOT') {
            handleScreenshot({ asset, token: token || undefined, VITE_DOMAIN: import.meta.env.VITE_DOMAIN });
          }
        });
      }
    },
    { once: true }  // Automatically removes after first call
  );

  window.addEventListener(
    'replate:editor:loaded',
    () => {
      hideLoader();
      emitToReactNative('viewerLoad');
    }
  );

  window.addEventListener('replate:viewer:error', () => {
    hideLoader();
    showErrorPage();
    emitToReactNative('viewerError');
  });
}

/**
 * Main entry point - wait for window load, then initialize
 */
registerConsoleHelpers();

window.addEventListener('load', async function () {
  setTimeout(() => {
    initComponent();
  }, 1);
});

// Export for testing
export {
  initComponent,
  validateSession,
  getViewportDimensions,
  getAssetType,
  resolveSize,
  getViewerElement,
  getCurrentSceneData,
  getCurrentSceneMode,
  registerConsoleHelpers
};
